// apps/client/src/components/common/DatePicker.tsx
// Dark-themed single-date picker built on react-day-picker.
// Mirrors the dark styling of DateRangePicker.tsx (do not hand-roll a calendar /
// use the native OS <input type="date"> calendar, which can't be themed).
// Value and onChange use the 'YYYY-MM-DD' string format used throughout the app.

import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import 'react-day-picker/dist/style.css';

export interface DatePickerProps {
  value: string | undefined; // 'YYYY-MM-DD'
  onChange: (d: string | undefined) => void; // returns 'YYYY-MM-DD' | undefined
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}

const parseYMD = (dateStr: string | undefined): Date | undefined => {
  if (!dateStr) return undefined;
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : undefined;
};

const toYMD = (date: Date | undefined): string | undefined => {
  if (!date) return undefined;
  return format(date, 'yyyy-MM-dd');
};

export default function DatePicker({
  value,
  onChange,
  id = 'date-picker',
  placeholder = 'Select date',
  disabled = false,
}: DatePickerProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = parseYMD(value);

  const displayValue = (): string => {
    if (!selected) return placeholder;
    return format(selected, 'dd/MM/yyyy');
  };

  const handleSelect = (day: Date | undefined) => {
    onChange(toYMD(day));
    if (day) setIsOpen(false);
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className={selected ? 'text-white' : 'text-gray-400'}>{displayValue()}</span>
        <CalendarIcon className="h-4 w-4 text-blue-400 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-4">
          <style>
            {`
              .rdp-dp {
                --rdp-cell-size: 36px;
                --rdp-accent-color: #2563eb;
                --rdp-background-color: #1f2937;
                margin: 0;
              }
              .rdp-dp .rdp-months { display: flex; }
              .rdp-dp .rdp-month { margin: 0; }
              .rdp-dp .rdp-caption {
                display: flex;
                justify-content: center;
                padding: 0 0.5rem;
                margin-bottom: 0.5rem;
              }
              .rdp-dp .rdp-caption_label {
                font-size: 0.875rem;
                font-weight: 500;
                color: white;
              }
              .rdp-dp .rdp-nav { display: flex; gap: 0.25rem; }
              .rdp-dp .rdp-nav_button {
                width: 28px;
                height: 28px;
                background: transparent;
                border: none;
                border-radius: 4px;
                color: #9ca3af;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .rdp-dp .rdp-nav_button:hover { background: #374151; color: white; }
              .rdp-dp .rdp-table { border-collapse: collapse; }
              .rdp-dp .rdp-head_cell {
                font-size: 0.75rem;
                font-weight: normal;
                color: #9ca3af;
                padding: 0.25rem 0;
                text-align: center;
                width: var(--rdp-cell-size);
              }
              .rdp-dp .rdp-cell { padding: 0; }
              .rdp-dp .rdp-day {
                width: var(--rdp-cell-size);
                height: var(--rdp-cell-size);
                font-size: 0.875rem;
                color: #d1d5db;
                border-radius: 6px;
                cursor: pointer;
                background: transparent;
                border: none;
              }
              .rdp-dp .rdp-day:hover:not(.rdp-day_selected):not(.rdp-day_disabled) {
                background: #374151;
              }
              .rdp-dp .rdp-day_today {
                border: 1px solid #3b82f6;
                color: #60a5fa;
              }
              .rdp-dp .rdp-day_selected {
                background: #2563eb !important;
                color: white !important;
              }
              .rdp-dp .rdp-day_outside { color: #4b5563; }
              .rdp-dp .rdp-day_disabled { color: #4b5563; cursor: not-allowed; }
            `}
          </style>
          <DayPicker
            className="rdp-dp"
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={1}
            showOutsideDays
            defaultMonth={selected}
          />
          <div className="mt-3 flex justify-between border-t border-gray-700 pt-3">
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
              }}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(toYMD(new Date()));
                setIsOpen(false);
              }}
              className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
